import React, {
  createContext,
  useRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";

import { getBookCover, makeResourceUrl, makeWaveformsJsonUrl } from "../utils/url/url";
import { useObserverPattern } from "../hooks/useObserverPattern";
import { logOtherEvent, OtherLogEvents } from "../api/analitics";
import { clutch } from "../utils/number/number";
import {
  selectorPlayerBrokenAudios,
  selectorPlayerWaveformsDictionary
} from "../store/player/selectors";
import { _sharedActionsPlayer } from "../store/player/actions";
import { _sharedActionsSystem } from "../store/system/actions";
import { _sharedActionsLibrary } from "../store/library/actions";
import { CUSTOM_EVENT_TYPE_NEW_PULLED_AUDIO_FILE_SRC } from "../utils/sw/sw.constants";
import { selectorSystemOnLine } from "../store/system/selectors";
import { _sharedActionsSW } from "../store/sw/actions";
import {_sharedActionsSettings} from "../store/settings/actions";
import {useGetCurrPrevNextChapters} from "./AudioContext.hooks";
import {
  filterTOCToAudioChapters,
  getAudioChapterByChapterId,
  getFileDuration,
} from "../utils/player";
import { useBookWithSWFallback } from "../hooks/pwa/sw";
import { PLAYBACK_DEFAULT } from "./AudioContext.constants";
import { SETTING } from "../utils/settings/constants";
import { isDevelopment } from "@/shared/utils/assembly";
import { getIsIOSDevice } from "@/shared/utils/device-detect";
import { selectorWaveformsDownloadedIds } from "@/shared/store/sw/selectorWaveformsDownloadedIds";
import { selectorAudiosDownloadedIds } from "@/shared/store/sw/selectorAudiosDownloadedIds";

export const PLAYER_EVENTS = {
  END: 2,
  CAN_PLAY_THROUGH: 3,
  PAUSE: 4,
  PLAY: 5,
  SKIP: 6,
  SEEK: 7,
  PLAY_CHAPTER: 8,
};

const playerID = Date.now().toString();

const AudioContext = createContext({});

const scope = "AudioContext";
const srcSilence = "/audio/silence.mp3";

const playerStateInitial = {
  chapterId: "",
  volume: 1,
  duration: 0,
  isPlaying: false,
  isMute: false,
  playbackRate: PLAYBACK_DEFAULT,
  fromML: false,
};

// Prevent apply IOS fixes in dev mode, because IOS version less than 14 freeze website
// for approximately 20 seconds when trying to play an audio.
const isApplyIOSFixes = !isDevelopment();

export const AudioProvider = ({ analytics, children }) => {
  const dispatch = useDispatch();
  const audioRef = useRef(null);

  const [playerState, _setPlayerState] = useState(playerStateInitial);
  const [swPulledBase64, setSwPulledBase64] = useState({});
  const {
    chapterId, isPlaying,
    volume, isMute, playbackRate, duration, fromML,
  } = playerState;

  const waveformsDictionary = useSelector(selectorPlayerWaveformsDictionary);
  const onLine = useSelector(selectorSystemOnLine);
  const audiosDownloadedIds = useSelector(selectorAudiosDownloadedIds);
  const waveformsDownloadedIds = useSelector(selectorWaveformsDownloadedIds);
  const brokenAudios = useSelector(selectorPlayerBrokenAudios);
  const {
    addObserverListener,
    removeObserverListener,
    notifyObserverListeners,
  } = useObserverPattern();
  const { book, toc } = useBookWithSWFallback(chapterId);
  const audioChapters = useMemo(() => filterTOCToAudioChapters(toc), [toc]);
  const audioChapter = useMemo(
    () => getAudioChapterByChapterId(toc, chapterId), [toc, chapterId]
  );
  const audioChapterId = audioChapter?.id;

  const isUseCachedAudioFile = audiosDownloadedIds.includes(audioChapterId);

  const [src, fileDuration, trackTitle = ""] = useMemo(() => {
    if (audioChapter) {
      const srcFinal = isUseCachedAudioFile
        ? swPulledBase64.key === audioChapterId
          ? swPulledBase64.value
          : undefined
        : makeResourceUrl(audioChapter.mp3);

      return [srcFinal, getFileDuration(audioChapter.mp3), audioChapter.title];
    }
    return [];
  }, [audioChapterId, isUseCachedAudioFile, audioChapter, swPulledBase64]);
  const cover = book ? getBookCover(book, true) : undefined;
  const isBroken = brokenAudios.includes(audioChapterId);

  const waveform = waveformsDictionary[audioChapterId]?.waveform || null;
  const paragraphsAudioPositions = waveformsDictionary[audioChapterId]
    ?.paragraphsAudioPositions || null;

  const dispatchPlayerEvent = (event, data = undefined) => {
    let prevented = false;

    notifyObserverListeners({
      ...data,
      event,
      preventEvent: () => {
        prevented = true;
      },
    });

    return prevented;
  }

  const getAudioElement = () => audioRef.current;

  const getCurrentTime = () => {
    const player = getAudioElement();
    if (player) {
      return player.currentTime;
    }
    return 0;
  };

  // Fetch paragraphs audio positions (and chapter audio wave form as there is the single API).
  // Waveforms data is needed to draw audio wave, detect para position in the track or vice-versa.
  // It is needed in the Reader and Big Audio Player only, so its call is optional.
  const downloadOrPullFromSWWaveformsData = (audioTrack) => {
    if (audioTrack) {
      if (waveformsDownloadedIds.includes(audioTrack.id)) {
        dispatch(_sharedActionsSW.pullProgressWaveform(audioTrack.id));
      } else {
        if (onLine) {
          dispatch(
            _sharedActionsPlayer.fetchWaveformsDictionary(
              makeWaveformsJsonUrl(audioTrack.mp3), audioTrack.id
            )
          );
        }
      }
    }
  };

  const currPrevNextChapters = useGetCurrPrevNextChapters(
    isPlaying,
    playbackRate,
    getCurrentTime,
    audioChapterId,
    toc,
    waveformsDictionary,
    addObserverListener,
    removeObserverListener,
    downloadOrPullFromSWWaveformsData,
  );

  const canSeekBack = !!currPrevNextChapters.chapterSeekPrev?.audioChapter;
  const canSeekForward = !!currPrevNextChapters.chapterSeekNext?.audioChapter;

  const saveHistoryListenCurrentPosition = (delay) => {
    const chapter = chapterId;

    if (chapter) {
      dispatch(_sharedActionsLibrary.saveListen(
        chapter, getCurrentTime(), fromML, delay
      ));
    }
  };

  const setCurrentTime = (position) => {
    if (position === undefined) {
      return;
    }

    const player = getAudioElement();
    if (player) {
      player.currentTime = position || 0;
    }
  };

  const _setPlayerVolume = (volume) => {
    const player = getAudioElement();

    if (!player) {
      return;
    }

    const volumeFinal = parseFloat(volume) || 1;
    if (typeof volumeFinal === "number" && volumeFinal !== player.volume) {
      player.volume = volumeFinal;
    }
  };

  const _setPlayerPlayback = (playbackRate) => {
    const player = getAudioElement();
    if (player) {
      player.playbackRate = playbackRate;
    }
  };

  const setStateWithPreservingOldValues = (newState) => {
    _setPlayerState((oldState) => ({
      ...oldState,
      ...newState,
    }));
  };

  const updatePlaybackRate = (value) => {
    setStateWithPreservingOldValues({
      playbackRate: value,
    });
  };

  const _play = (onError = undefined) => {
    const player = getAudioElement();
    if (player) {
      player
        .play()
        .then(() => {
          dispatch(_sharedActionsSettings.updateSettings(
            SETTING.playerState.id, playerID,
          ));
          setStateWithPreservingOldValues({ isPlaying: true });

          saveHistoryListenCurrentPosition(1000);
        })
        .catch(onError);
    }
  };

  const pause = () => {
    saveHistoryListenCurrentPosition(1000);

    const player = getAudioElement();
    if (player) {
      player.pause();
      setStateWithPreservingOldValues({ isPlaying: false });
    }
  };

  // Fixes:
  // – "canplaythrough" is fired with empty source (src) when changing from source A to source B.
  const fireFunctionAfterValidSrcAttached = (cb) => {
    const player = getAudioElement();

    if (player) {
      player.addEventListener("canplaythrough", () => {
        if (player.src) {
          if (cb) {
            cb();
          }
        } else {
          fireFunctionAfterValidSrcAttached(cb);
        }
      }, { once: true });
    }
  };

  const setActiveChapter = (id, startTime, options) => {
    setStateWithPreservingOldValues({
      chapterId: id,
      fromML: options?.fromML,
    });

    pause();
    setCurrentTime(startTime);

    const handleAttachedChapterSRC = () => {
      _setPlayerPlayback(playbackRate);
      setCurrentTime(startTime);

      if (options?.onChapterSRCAttached) {
        options.onChapterSRCAttached();
      }
    };

    if (id === chapterId) {
      handleAttachedChapterSRC();
    } else {
      fireFunctionAfterValidSrcAttached(handleAttachedChapterSRC);
    }
  };

  const _playChapter = (id, startTime, options) => {
    dispatchPlayerEvent(PLAYER_EVENTS.PLAY_CHAPTER, {
      position: startTime,
    });

    setActiveChapter(id, startTime, {
      fromML: options?.fromML,
      onChapterSRCAttached: () => {
        _play(options?.onError);
      },
    });
  };
  
  const toggleChapterPlaying = (id, startTime, options) => {
    if (id === undefined || id === chapterId) {
      if (isPlaying) {
        pause();
      } else {
        _play(options?.onError);
      }
    } else {
      _playChapter(id, startTime, options)
    }
  };

  const playChapterAndShowPlayer = (id, startTime, options) => {
    _playChapter(id, startTime, options);
    dispatch(_sharedActionsPlayer.setIsSmallPlayerVisible(true));
  };

  const stop = () => {
    setStateWithPreservingOldValues({
      chapterId: "",
    });

    dispatch(_sharedActionsPlayer.setIsSmallPlayerVisible(false));

    const player = getAudioElement();
    if (player) {
      if (!player.paused) {
        pause();
      }
    }
  };

  const skip = (skipValue) => {
    const player = getAudioElement();

    if (player) {
      setCurrentTime(clutch(getCurrentTime() + skipValue, 0, duration));

      dispatchPlayerEvent(PLAYER_EVENTS.SKIP);
      saveHistoryListenCurrentPosition(1000);
    }
  };

  const seek = (forvard) => {
    const seekChapterData = forvard
      ? currPrevNextChapters.chapterSeekNext
      : currPrevNextChapters.chapterSeekPrev;

    if (seekChapterData && seekChapterData.chapter && seekChapterData.audioChapter) {
      if (seekChapterData.audioChapter.id === audioChapterId) {
        setCurrentTime(seekChapterData.positions?.start ?? 0);
        dispatchPlayerEvent(PLAYER_EVENTS.SKIP);
      } else {
        const chapterIdNew = seekChapterData.chapter.id;

        playChapterAndShowPlayer(chapterIdNew, seekChapterData.positions?.start ?? 0);
        dispatchPlayerEvent(PLAYER_EVENTS.SEEK, {
          chapterIdNew,
        });
      }

      saveHistoryListenCurrentPosition(1000);
      return;
    }

    pause();
  };

  const _handlePlayedToEnd = () => {
    const prevented = dispatchPlayerEvent(PLAYER_EVENTS.END);

    if (!prevented) {
      seek(true);
    }
  };

  const _handleLoadedMetaData = (event) => {
    setStateWithPreservingOldValues({ duration: event.target.duration });
  };

  const _handlePlay = () => {
    dispatchPlayerEvent(PLAYER_EVENTS.PLAY);

    if (brokenAudios.includes(chapterId)) {
      // Remove from the broken audios array.
      const audioIndex = brokenAudios.findIndex((item) => item === chapterId);
      if (audioIndex !== -1) {
        brokenAudios.splice(audioIndex, 1);
        dispatch(_sharedActionsPlayer.setBrokenAudios([...brokenAudios]));
      }
    }
  };

  const _handlePause = () => {
    dispatchPlayerEvent(PLAYER_EVENTS.PAUSE);
  };

  const errorHandler = () => {
    setStateWithPreservingOldValues({
      chapterId,
      volume: 1,
    });
    pause();

    if (!brokenAudios.includes(chapterId)) {
      // Add to the broken audios array.
      dispatch(_sharedActionsPlayer.setBrokenAudios([...brokenAudios, chapterId]));
    }

    if (onLine) {
      dispatch(_sharedActionsSystem.toast("@audioNotAvailable"));
    }
  };

  const refFunctions = useRef({
    downloadOrPullFromSWWaveformsData,
    fireFunctionAfterValidSrcAttached,
    _play,
    pause,
    _setPlayerVolume,
    _setPlayerPlayback,
    getCurrentTime,
    skip,
    seek,
  });

  // analytics
  useEffect(() => {
    if (analytics && chapterId) {
      logOtherEvent(OtherLogEvents.LISTEN_CHAPTER, {
        id: chapterId,
      });
    }
  }, [analytics, chapterId]);

  // Fixes the IOS can't play the audio before the interaction with the page.
  // Reproduce: use Iphone, go to Book List with shown bookshelf under the items, refresh the page,
  // click on the play icon, see thrown error in the console and audio hadn't started playing.
  // https://stackoverflow.com/a/64750678
  useEffect(() => {
    if (getIsIOSDevice() && isApplyIOSFixes) {
      const player = getAudioElement();

      if (player) {
        player.src = srcSilence;
        player.play().catch(() => {});
        player.pause();
        player.currentTime = 0;
        document.addEventListener(
          "touchstart",
          () => {
            if (player.src.includes(srcSilence)) {
              player
                .play()
                .then(() => {
                  player.pause();
                })
                .catch(() => {});
            }
          },
          { once: true },
        );
      }
    }
  }, []);

  // Fix audio is not loading and "canplaythrough" event is not fired when src is set on the IOS.
  // e.g. just start playing in Reader – it requires 2nd click on play btn.
  // e.g. start playing in Reader, change chapter – it requires 2nd click on play btn.
  // https://stackoverflow.com/a/49794011
  useEffect(() => {
    if (src && getIsIOSDevice() && isApplyIOSFixes) {
      getAudioElement().load();
    }
  }, [src]);
  // ==================================================


  // Sets the first possible audio chapter if no chapter was found.
  useEffect(() => {
    if (chapterId && !audioChapter && audioChapters?.length) {
      setStateWithPreservingOldValues({ chapterId: audioChapters[0].id });
    }
  }, [chapterId, audioChapter, audioChapters]);

  // Fetches chapter paragraphs positions in the audio (and waveform),
  // to be able to navigate (seek) between subchapters.
  useEffect(() => {
    if (audioChapter) {
      downloadOrPullFromSWWaveformsData(audioChapter);
    }
  }, [audioChapter]);
  
  // Pauses the playing if started playing in another app tab.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === SETTING.playerState.id) {
        if (isPlaying && e.newValue !== playerID) {
          refFunctions.current.pause();
        }
      }
    };

    window.addEventListener("storage", handler);

    return () => {
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Attaches {mediaSession} controls events.
  useEffect(() => {
    if ("mediaSession" in navigator && cover) {
      if (window.MediaMetadata) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          pageTitle: book?.title,
          album: trackTitle,
          artwork: [{ src: cover, type: "image/png" }],
        });
      }

      navigator.mediaSession.setActionHandler("play", () => {
        refFunctions.current._play();
      });
      navigator.mediaSession.setActionHandler("pause", () => {
        refFunctions.current.pause();
      });
      navigator.mediaSession.setActionHandler("seekbackward", () => {
        refFunctions.current.skip(-10);
      });
      navigator.mediaSession.setActionHandler("seekforward", () => {
        refFunctions.current.skip(10);
      });
      navigator.mediaSession.setActionHandler("previoustrack", () => {
        refFunctions.current.seek(false);
      });
      navigator.mediaSession.setActionHandler("nexttrack", () => {
        refFunctions.current.seek(true);
      });
    }
  }, [cover, book?.title, trackTitle]);

  // Updates HTMLElement volume when it is updated in the state.
  useEffect(() => {
    _setPlayerVolume(volume);
  }, [volume]);

  // Updates HTMLElement playbackRate when it is updated in the state.
  useEffect(() => {
    _setPlayerPlayback(playbackRate);
  }, [playbackRate]);

  // Pulls audio file base64.
  useEffect(() => {
    if (!onLine && !isUseCachedAudioFile && audioChapterId) {
      dispatch(_sharedActionsSystem.toast(
        ["@sw:noInternetConnection", ". ", "@sw:featureNotAvailable", "."]
      ));
    }

    if (isUseCachedAudioFile) {
      dispatch(_sharedActionsSW.pullAudio(audioChapterId));
      dispatch(_sharedActionsSW.pullProgressWaveform(audioChapterId));
    }
  }, [onLine, isUseCachedAudioFile, audioChapterId]);

  // Updates pulled audio base64.
  useEffect(() => {
    const listener = (e) => {
      setSwPulledBase64(e.detail);

      if (isPlaying) {
        refFunctions.current.fireFunctionAfterValidSrcAttached(() => {
          refFunctions.current._play();
        });
      }
    };

    window.addEventListener(CUSTOM_EVENT_TYPE_NEW_PULLED_AUDIO_FILE_SRC, listener);
    return () => {
      window.removeEventListener(CUSTOM_EVENT_TYPE_NEW_PULLED_AUDIO_FILE_SRC, listener);
    };
  }, []);

  return (
    <AudioContext.Provider
      value={{
        saveHistoryListenCurrentPosition,

        setActiveChapter,
        playChapterAndShowPlayer,
        toggleChapterPlaying,

        pausePlayer: pause,
        stopPlayer: stop,

        getPlayerCurrentTime: getCurrentTime,
        setPlayerCurrentTime: setCurrentTime,

        updatePlayerState: setStateWithPreservingOldValues,
        updatePlaybackRate,

        seekPlayer: seek,
        skipPlayer: skip,

        downloadOrPullFromSWWaveformsData,
        subscribeToPlayerEvents: addObserverListener,
        unsubscribeFromPlayerEvents: removeObserverListener,

        chapterId,
        volume,
        duration,
        isPlaying,
        isMute,
        playbackRate,

        fileDuration,
        trackTitle,
        canSeekBack,
        canSeekForward,
        isBroken,

        // solves a problem of a wrong {chapterId} in the case of a duplicate chapter
        // e.g. {chapterId="127.5"}, but {audioChapterId="127.7"}
        audioChapterId,
        audioChapter,
        audioChapters,
        waveform,
        waveformsDictionary,
        paragraphsAudioPositions,
        currPrevNextChapters,
        toc,
        book,
      }}>
      {children}
      <audio
        ref={audioRef}
        muted={isMute}
        title={trackTitle}
        src={src}
        onPlay={_handlePlay}
        onPause={_handlePause}
        onLoadedMetadata={_handleLoadedMetaData}
        onCanPlayThrough={() => {
          dispatchPlayerEvent(PLAYER_EVENTS.CAN_PLAY_THROUGH);
        }}
        onEnded={_handlePlayedToEnd}
        onError={errorHandler}
      />
    </AudioContext.Provider>
  );
};

AudioProvider.displayName = scope;

AudioProvider.propTypes = {
  children: PropTypes.node,
  analytics: PropTypes.bool, // defines whether send listen analytics or not
};

/**
 * @returns {{
    playChapterAndShowPlayer: (
      id: string,
      startTime?: number,
      options?: {
        fromML?: boolean,
        onError?: function,
      }
    ) => void,
    setActiveChapter: function,
    playChapterAndShowPlayerIfNotTheSameBook: function,
    toggleChapterPlaying: function,
    seekPlayer: function,
    skipPlayer: function,
    stopPlayer: function,

    getPlayer: function,
    getPlayerCurrentTime: function,
    setPlayerCurrentTime: function,
    updatePlayerState: function,
    updatePlaybackRate: function,
    downloadOrPullFromSWWaveformsData: function,
    savePlayPosition: function,

    subscribeToPlayerEvents: function,
    unsubscribeFromPlayerEvents: function,

    chapterId: string,
    volume: number,
    duration: number,
    isPlaying: boolean,
    stopTimeout: number,
    isMute: boolean,
    playbackRate: number,

    fileDuration: number|undefined,
    trackTitle: string,
    canSeekBack: boolean,
    canSeekForward: boolean,
    isBroken: boolean,

    audioChapterId: string|undefined,
    audioChapter: {}|null,
    waveform: {}|null,
    waveformsDictionary: {},
    paragraphsAudioPositions: []|null,
    audioChapters: {}[],
    currPrevNextChapters: CurrPrevNextChapters,
    toc: {}[],
    book: {},
 * }}
 */
export const useAudioContext = () => useContext(AudioContext);
